<template>
  <div>
    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="modal is-active">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-content">
        <div class="success-card">
          <div class="success-icon-wrapper">
            <div class="success-icon">
              <i class="fas fa-check-circle"></i>
            </div>
          </div>
          <h2 class="success-title">
            <span class="emoji">🎉</span>
            <span class="title-text">Payment Successful!</span>
            <span class="emoji">🎉</span>
          </h2>
          <p class="success-submessage">
            Thank you for choosing
            <span class="brand-name">QuickAdmit</span>
            ! We're excited to have you on board.
          </p>
          <div class="success-actions">
            <button class="button is-primary is-medium" @click="goToSettings">
              <span>View Details</span>
              <i class="fas fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="closeModal"></button>
    </div>

    <section class="section">
      <div
        v-if="hasPastDueSubscription"
        class="container notification is-danger is-light has-text-centered mb-0 past-due-notification"
        style="border-radius: 0"
      >
        <span>
          Your subscription is past due. Please
          <a href="#" @click="onManageSubscription"><strong>Update Payment Method</strong></a>
          .
        </span>
      </div>
    </section>

    <div class="container is-max-hd">
      <div v-if="isManager">
        <div v-if="hasActiveSubscription">
          <SubscriptionChange />
        </div>
        <div v-else>
          <SubscriptionSelect />
        </div>
      </div>
      <div v-else class="column is-three-fifths is-offset-one-fifth">
        <section class="section">
          <div class="box">
            <div class="block">
              <div class="has-text-centered">
                <h1 class="title is-3 has-text-primary">Organization Subscription</h1>
                <p class="block">
                  <strong>{{ organization }}</strong>
                  does not have a current subscription. Your organization manager has been notified.
                </p>

                <p>Please try again later.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SubscriptionChange from '../components/SubscriptionChange';
import SubscriptionSelect from '../components/SubscriptionSelect';
import request from '../request';

export default {
  name: 'PlanList',
  components: {
    SubscriptionChange,
    SubscriptionSelect,
  },
  data() {
    return {
      loading: false,
      showSuccessModal: false,
    };
  },
  computed: {
    ...mapGetters(['organization', 'isManager', 'hasActiveSubscription', 'hasPastDueSubscription']),
  },
  mounted() {
    // Check if returning from Stripe checkout
    if (this.$route.query.session_id) {
      this.showSuccessModal = true;
      // Clean up the URL
      this.$router.replace({ name: 'PlanList' });
      // Trigger confetti
      this.launchConfetti();
    }
  },
  methods: {
    onManageSubscription() {
      this.loading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
    closeModal() {
      this.showSuccessModal = false;
    },
    goToSettings() {
      this.$router.push({ name: 'OrganizationDetail' });
    },
    launchConfetti() {
      const particleCount = 30; // Number of confetti pieces per burst
      const colors = ['#48bb78', '#4299e1', '#ed8936', '#9f7aea', '#f56565', '#ffd700'];

      // Create multiple burst points strategically placed
      const burstPoints = [
        { x: 15, y: 20 }, // Top-left
        { x: 85, y: 20 }, // Top-right
        { x: 50, y: 30 }, // Top-center
        { x: 20, y: 60 }, // Middle-left
        { x: 80, y: 60 }, // Middle-right
      ];

      // Create confetti burst from each point with slight delay
      burstPoints.forEach((point, index) => {
        setTimeout(() => {
          for (let i = 0; i < particleCount; i++) {
            this.createConfettiBurst(colors, point.x, point.y);
          }
        }, index * 100); // Stagger bursts by 100ms
      });
    },
    createConfettiBurst(colors, startX, startY) {
      const particle = document.createElement('div');
      particle.className = 'confetti-particle';

      // Random color
      particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];

      // Random angle (360 degrees - burst in all directions)
      const angle = Math.random() * Math.PI * 2;
      const velocity = 10 + Math.random() * 15; // Random velocity

      // Calculate end position
      const deltaX = Math.cos(angle) * velocity;
      const deltaY = Math.sin(angle) * velocity;

      particle.style.left = `${startX}%`;
      particle.style.top = `${startY}%`;
      particle.style.setProperty('--deltaX', `${deltaX}vw`);
      particle.style.setProperty('--deltaY', `${deltaY}vh`);

      // Random size
      const size = 8 + Math.random() * 6;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;

      // Random rotation duration
      particle.style.animationDuration = `${1.5 + Math.random() * 1}s`;

      document.body.appendChild(particle);

      setTimeout(() => particle.remove(), 3000);
    },
  },
};
</script>

<style scoped>
.past-due-notification {
  position: relative;
  top: -2rem;
  z-index: 20;
}

.success-card {
  background: white;
  border-radius: 20px;
  padding: 3rem 2.5rem;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
  box-shadow:
    0 20px 60px rgba(72, 187, 120, 0.15),
    0 10px 30px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(72, 187, 120, 0.2);
}

.success-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 1.5rem;
}

.success-icon {
  font-size: 5rem;
  color: #48bb78;
  position: relative;
  z-index: 2;
  animation: success-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes success-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  animation: slide-up 0.6s ease-out 0.2s both;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.success-title .title-text {
  color: #48bb78;
}

.success-title .emoji {
  font-size: 2rem;
}

.success-submessage {
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 2rem;
  line-height: 1.7;
  animation: slide-up 0.6s ease-out 0.3s both;
}

.brand-name {
  font-weight: 700;
  color: #2d3748;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes slide-up {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.success-actions .button {
  min-width: 200px;
}
</style>

<!-- Unscoped styles for confetti (since particles are added to body) -->
<style>
.confetti-particle {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  border-radius: 2px;
  animation: confetti-burst ease-out forwards;
}

@keyframes confetti-burst {
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translate(var(--deltaX), var(--deltaY)) rotate(720deg);
    opacity: 0;
  }
}
</style>
